import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CheckBox from "../common/CheckBox";
import { userRolesAdmin, userRolesSalesManager, userRolesSeller, userRolesSellerOwner, userRolesBALT, userRolesLaboratory, userRolesZO } from "./constants";
import TemplateSelector from "./TemplateSelector";
import UserRole from "./UserRole";
import { checkAdditionalRole, checkCrud } from "../../helpers/userRolesHelper";

require("./UserRoles.css");

interface Props {
    data: any;
    enabled?: boolean;
    onRolesChange: any;
    userInfo: any;
    isUserAdmin: boolean;
}

const UserRoles = (props: Props) => {
    const { t } = useTranslation(undefined, { keyPrefix: 'Users.Popup.UserRoles' });

    const cloneDeep = require('clone-deep');

    const [roles, setRoles] = useState(props.data);
    const [crudRoles, setCrudRoles] = useState([]);

    const [priceList, setPriceList] = useState(false);
    const [copyReceipt, setCopyReceipt] = useState(false);
    const [customReceipts, setCustomReceipts] = useState(false);
    const [cenik, setCenik] = useState(false);
    const [canSeeSellerTemplates, setCanSeeSellerTemplates] = useState(false);

    useEffect(() => {
        setRoles(props.data);
        const crudRoles = props.data?.filter(r => {
            return !r.isAdditionalRole;
        });

        setCrudRoles(crudRoles);
        const priceListRole = props.data?.find(r => {
            return r.name === 'priceList';
        });
        if (priceListRole) {
            setPriceList(priceListRole.isAuthorizedForAdditionalRole);
        }

        const copyReceiptRole = props.data?.find(r => {
            return r.name === 'copyReceipt';
        });
        if (copyReceiptRole) {
            setCopyReceipt(copyReceiptRole.isAuthorizedForAdditionalRole);
        }

        const customReceiptsRole = props.data?.find(r => {
            return r.name === 'customReceipts';
        });
        if (customReceiptsRole) {
            setCustomReceipts(customReceiptsRole.isAuthorizedForAdditionalRole);
        }

        const cenikRole = props.data?.find(r => {
            return r.name === 'cenik';
        });
        if (cenikRole) {
            setCenik(cenikRole.isAuthorizedForAdditionalRole);
        }

        var sellerCreator = checkCrud('seller', 'create', props.userInfo);
        var sellerEditor = checkCrud('seller', 'update', props.userInfo);

        if (sellerCreator && sellerEditor) {
            setCanSeeSellerTemplates(true);
        }



    }, [props.data])

    const handleCrudChange = (newCrud, roleName) => {

        const newRoles = roles;
        const roleToChange = newRoles.find(r => { return r.name === roleName });

        if (newCrud.indexOf('admin') >= 0) {
            newCrud = ['create', 'update', 'read', 'delete', 'admin']; // if admin is select, then auto select all
        }

        roleToChange.crud = newCrud;
        setRoles(newRoles);
        props.onRolesChange(newRoles);
    }

    const handleTemplateChange = (val) => {

        let newRoles;
        switch (val.value) {
            case 1:
                newRoles = cloneDeep(userRolesAdmin);
                break;
            case 2:
                newRoles = cloneDeep(userRolesSalesManager);
                break;
            case 3:
                newRoles = cloneDeep(userRolesSellerOwner);
                break;
            case 4:
                newRoles = cloneDeep(userRolesSeller);
                break;
            case 5:
                newRoles = cloneDeep(userRolesLaboratory);
                break;
            case 6:
                newRoles = cloneDeep(userRolesBALT);
                break;
            case 7:
                newRoles = cloneDeep(userRolesZO);
                break;
        }
        const crudRoles = newRoles?.filter(r => {
            return !r.isAdditionalRole;
        })
        if (crudRoles) {
            setCrudRoles(crudRoles.sort((a, b) => (a.name > b.name) ? 1 : -1));
            setRoles(newRoles);
            props.onRolesChange(newRoles);
        }
    }

    const handlePriceListRoleChange = (val) => {
        const newRoles = roles;
        const roleToChange = newRoles.find(r => { return r.name === 'priceList' });
        roleToChange.isAuthorizedForAdditionalRole = val;
        setRoles(newRoles);
        props.onRolesChange(newRoles);
        setPriceList(val);
    }

    const handleCopyReceiptRoleChange = (val) => {
        const newRoles = roles;
        const roleToChange = newRoles.find(r => { return r.name === 'copyReceipt' });
        roleToChange.isAuthorizedForAdditionalRole = val;
        setRoles(newRoles);
        props.onRolesChange(newRoles);
        setCopyReceipt(val);
    }

    const handleCustomReceiptsRoleChange = (val) => {
        const newRoles = roles;
        var roleToChange = newRoles.find(r => { return r.name === 'customReceipts' });

        if (!roleToChange) {
            roleToChange = { name: 'customReceipts', isAdditionalRole: true, isAuthorizedForAdditionalRole: val };
            newRoles.push(roleToChange);
        }
        else {
            roleToChange.isAuthorizedForAdditionalRole = val;
        }
        setRoles(newRoles);
        props.onRolesChange(newRoles);
        setCustomReceipts(val);
    }

    const handleCenikRoleChange = (val) => {
        const newRoles = roles;
        var roleToChange = newRoles.find(r => { return r.name === 'cenik' });

        if (!roleToChange) {
            roleToChange = { name: 'cenik', isAdditionalRole: true, isAuthorizedForAdditionalRole: val };
            newRoles.push(roleToChange);
        }
        else {
            roleToChange.isAuthorizedForAdditionalRole = val;
        }
        setRoles(newRoles);
        props.onRolesChange(newRoles);
        setCenik(val);
    }

    return (
        <>
            <div className="userRoles">
                <TemplateSelector
                    isUserAdmin={props.isUserAdmin}
                    canSeeSellerTemplates={canSeeSellerTemplates}
                    onChange={handleTemplateChange}
                />
                <div className="row">
                    <div className="col-sm-10">
                        <div className="row">
                            <div className="col-sm-2"></div>
                            <div className="col-sm-2">{t("Read")}</div>
                            <div className="col-sm-2">{t("Create")}</div>
                            <div className="col-sm-2">{t("Update")}</div>
                            <div className="col-sm-2">{t("Delete")}</div>
                            <div className="col-sm-2">{t("Admin")}</div>
                        </div>

                        {crudRoles?.map((role) => {

                            return (
                                <UserRole
                                    data={role}
                                    enabled={props.enabled}
                                    onCrudChange={handleCrudChange}
                                />
                            );
                        })}
                    </div>
                    <div className="col-sm-2">
                        <CheckBox
                            label={t('PriceListImport')}
                            defaultValue={priceList}
                            handleCheckedChange={handlePriceListRoleChange}
                            id={'priceListRoleCheckbox'}
                            paddingLeft={0}
                            disabled={!checkAdditionalRole('priceList', props.userInfo) && !props.isUserAdmin}
                        />
                        <CheckBox
                            label={t('CopyReceipt')}
                            defaultValue={copyReceipt}
                            handleCheckedChange={handleCopyReceiptRoleChange}
                            id={'copyReceiptRoleCheckbox'}
                            paddingLeft={0}
                            disabled={!checkAdditionalRole('copyReceipt', props.userInfo) && !props.isUserAdmin}
                        />
                        <CheckBox
                            label={t('CustomReceipts')}
                            defaultValue={customReceipts}
                            handleCheckedChange={handleCustomReceiptsRoleChange}
                            id={'customReceiptsRoleCheckbox'}
                            paddingLeft={0}
                            disabled={!checkAdditionalRole('customReceipts', props.userInfo) && !props.isUserAdmin}
                        />
                        <CheckBox
                            label={t('Cenik')}
                            defaultValue={cenik}
                            handleCheckedChange={handleCenikRoleChange}
                            id={'cenikRoleCheckbox'}
                            paddingLeft={0}
                            disabled={!checkAdditionalRole('cenik', props.userInfo) && !props.isUserAdmin}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default UserRoles;